<template>
  <div
    ref="containerEl"
    class="group relative w-full bg-black"
  >
    <video
      id="video-player"
      ref="videoEl"
      class="aspect-video w-full bg-black"
      playsinline
      muted
      autoplay
      crossOrigin="anonymous"
    />
    <ConfimMenu2
      v-if="adapter"
      :select-quality="setQuality"
      :adapter="adapter"
      :mute-unmute="muteUnmute"
      :update-volume="updateVolume"
      :play="play"
      :pause="pause"
      :seek="seek"
      :full-screen="fullScreen"
    />
  </div>
</template>

<script>
import ConfimMenu2 from '@/components/builder/elements/live-stream/ConfimMenu2.vue'

export default {
  name: 'LiveStreamPlayer',
  components: {ConfimMenu2},
  props: {
    section: Object
  },
  data () {
    return {
      closeSuggestionMessage: false,
      intervalId: null,
      retryTimer: null,
      videoEl: null,
      containerEl: null,
      adapter: null,
      isFullscreen: false,
      player: null,
      domListenersBound: false
    }
  },
  methods: {
    play () {
      this.player.play()
    },
    pause () {
      this.player.pause()
    },
    muteUnmute () {
      this.player.setMuted(!this.adapter.mute)
      this.adapter.mute = !this.adapter.mute
    },
    updateVolume (e) {
      this.player.setVolume(e.target.value)
      this.adapter.volume = e.target.value
    },
    async fullScreen () {
      if (!document.fullscreenElement &&
          !document.webkitFullscreenElement &&
          !document.mozFullScreenElement &&
          !document.msFullscreenElement) {

        // Входим в fullscreen
        try {
          const element = this.$refs.containerEl

          if (element.requestFullscreen) {
            await element.requestFullscreen()
          } else if (element.webkitRequestFullscreen) {
            // Safari
            await element.webkitRequestFullscreen()
          } else if (element.webkitEnterFullscreen) {
            // iOS Safari video element
            await this.$refs.videoEl.webkitEnterFullscreen()
          } else if (element.mozRequestFullScreen) {
            // Firefox
            await element.mozRequestFullScreen()
          } else if (element.msRequestFullscreen) {
            // IE/Edge
            await element.msRequestFullscreen()
          }

          // Для iOS устройств, где fullscreen API может не поддерживаться
          if ('webkitSupportsFullscreen' in this.$refs.videoEl && this.$refs.videoEl.webkitSupportsFullscreen) {
            this.$refs.videoEl.webkitEnterFullscreen()
          }

          this.adapter.fullScreen = true
          this.isFullscreen = true
        } catch (error) {
          console.error('Ошибка при входе в fullscreen:', error)

          // Fallback для устройств без поддержки Fullscreen API
          if (this.$refs.videoEl.webkitEnterFullscreen) {
            this.$refs.videoEl.webkitEnterFullscreen()
          }
        }
      } else {
        // Выходим из fullscreen
        try {
          if (document.exitFullscreen) {
            await document.exitFullscreen()
          } else if (document.webkitExitFullscreen) {
            // Safari
            await document.webkitExitFullscreen()
          } else if (document.webkitCancelFullScreen) {
            // Старый Safari
            await document.webkitCancelFullScreen()
          } else if (document.mozCancelFullScreen) {
            // Firefox
            await document.mozCancelFullScreen()
          } else if (document.msExitFullscreen) {
            // IE/Edge
            await document.msExitFullscreen()
          }

          // Для iOS
          if ('webkitExitFullscreen' in this.$refs.videoEl) {
            this.$refs.videoEl.webkitExitFullscreen()
          }

          this.adapter.fullScreen = false
          this.isFullscreen = false
        } catch (error) {
          console.error('Ошибка при выходе из fullscreen:', error)
        }
      }
    },
    handleFullscreenChange () {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      )

      this.adapter.fullScreen = isCurrentlyFullscreen
      this.isFullscreen = isCurrentlyFullscreen

      // Для мобильных устройств можем добавить блокировку ориентации
      if (isCurrentlyFullscreen && screen.orientation && screen.orientation.lock) {
        // Пытаемся заблокировать в landscape режиме
        screen.orientation.lock('landscape').catch(() => {
          // Игнорируем ошибку, если не поддерживается
        })
      } else if (!isCurrentlyFullscreen && screen.orientation && screen.orientation.unlock) {
        // Разблокируем ориентацию при выходе
        screen.orientation.unlock()
      }
    },
    seek () {
      this.player.seekTo(0)
    },
    setQuality (_quality) {
      if (_quality === 'auto') {
        this.player.setAutoQualityMode(true)
        this.adapter.isAuto = true
      } else {
        const quality = this.player.getQualities().find(q => _quality === q.height)
        this.player.setQuality(quality, true)
        this.adapter.isAuto = false
        this.adapter.quality = quality
      }
    },
    checkLiveSync () {
      if (!this.player) return

      this.adapter.quality = this.player.getQuality()
      this.adapter.playbackSpeed = this.player.getPlaybackRate()
      this.adapter.latency = this.player.getLiveLatency()
    },
    clearRetryTimer () {
      if (this.retryTimer) {
        clearTimeout(this.retryTimer)
        this.retryTimer = null
      }
    },
    buildPlaybackUrl (forceRefresh = false) {
      const url = this.section?.playbackUrl || ''

      if (!url || !forceRefresh) {
        return url
      }

      const nextUrl = new URL(url, window.location.origin)
      nextUrl.searchParams.set('_reload', Date.now().toString())

      return nextUrl.toString()
    },
    loadPlaybackSource (forceRefresh = false) {
      if (!this.player || !this.section?.playbackUrl) {
        return
      }

      this.player.load(this.buildPlaybackUrl(forceRefresh))
    },
    handleVideoPlay () {
      if (this.adapter) {
        this.adapter.play = true
      }
    },
    handleVideoPause () {
      if (this.adapter) {
        this.adapter.play = false
      }
    },
    handleVideoPlaying () {
      this.clearRetryTimer()
      this.closeSuggestionMessage = false
    },
    handleContainerDoubleClick (e) {
      if (e.target === this.$refs.videoEl || e.target === this.containerEl) {
        this.fullScreen()
      }
    },
    bindDomListeners () {
      if (this.domListenersBound || !this.$refs.videoEl) {
        return
      }

      this.$refs.videoEl.addEventListener('play', this.handleVideoPlay)
      this.$refs.videoEl.addEventListener('pause', this.handleVideoPause)
      this.$refs.videoEl.addEventListener('playing', this.handleVideoPlaying)

      document.addEventListener('fullscreenchange', this.handleFullscreenChange)
      document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange)
      document.addEventListener('mozfullscreenchange', this.handleFullscreenChange)
      document.addEventListener('MSFullscreenChange', this.handleFullscreenChange)

      if (this.$refs.videoEl) {
        this.$refs.videoEl.addEventListener('webkitbeginfullscreen', () => {
          if (this.adapter) {
            this.adapter.fullScreen = true
          }
          this.isFullscreen = true
        })

        this.$refs.videoEl.addEventListener('webkitendfullscreen', () => {
          if (this.adapter) {
            this.adapter.fullScreen = false
          }
          this.isFullscreen = false
        })
      }

      if (this.containerEl) {
        this.containerEl.addEventListener('dblclick', this.handleContainerDoubleClick)
      }

      this.domListenersBound = true
    },
    syncAdapter () {
      const quality = this.player.getQuality()
      const levels = this.player.getQualities() || []

      if (!this.adapter) {
        this.adapter = {
          isAuto: true,
          play: false,
          quality,
          levels,
          volume: 1,
          mute: true,
          fullScreen: false
        }
        return
      }

      this.adapter.quality = quality
      this.adapter.levels = levels
    },
    async isLiveActive () {
      const playbackUrl = this.section?.playbackUrl

      if (!playbackUrl) {
        return false
      }

      try {
        // HEAD avoids downloading the manifest; cache bust prevents stale CDN response
        const res = await fetch(playbackUrl + (playbackUrl.includes('?') ? '&' : '?') + 't=' + Date.now(), {
          method: 'HEAD',
          cache: 'no-store'
        })
        return res.ok
      } catch (e) {
        return false
      }
    },
    startLiveProbe () {
      this.clearRetryTimer()

      const tick = async () => {
        const alive = await this.isLiveActive(this.section.playbackUrl)

        if (alive) {
          try {
            this.loadPlaybackSource(true)
          } catch (error) {
            console.error('Failed to reload live stream playback:', error)
          }
        }

        this.retryTimer = setTimeout(tick, alive ? 2000 : 3000)
      }

      this.retryTimer = setTimeout(tick, 0)
    },
    handlePlayerReady () {
      try { this.player.setMuted(true) } catch (e) {console.error(e)}

      this.play()

      if (!this.intervalId) {
        this.intervalId = setInterval(this.checkLiveSync, 2000)
      }

      this.syncAdapter()

      const max720pQuality = (this.player.getQualities() || []).find(q => q.height === 720)
      if (max720pQuality) {
        this.player.setAutoMaxQuality(max720pQuality)
      }
    },
    handlePlayerError (e) {
      console.error('Live stream playback error:', e)
      this.closeSuggestionMessage = true
      this.startLiveProbe()
    }
  },
  watch: {
    'section.playbackUrl' (val, oldVal) {
      if (!this.player || !val || val === oldVal) {
        return
      }

      this.clearRetryTimer()

      try {
        this.loadPlaybackSource(true)
      } catch (error) {
        console.error('Failed to update live stream playback url:', error)
        this.startLiveProbe()
      }
    }
  },
  mounted () {
    if (window.IVSPlayer.isPlayerSupported) {
      this.containerEl = this.$refs.containerEl
      this.player = window.IVSPlayer.create({
        serviceWorker: {
          url: '/amazon-ivs-service-worker-loader.js'
        }
      })

      // Ensure autoplay passes browser policies
      try { this.player.setMuted(true) } catch (e) {console.error(e)}
      if (this.$refs.videoEl) {
        this.$refs.videoEl.muted = true
        this.$refs.videoEl.setAttribute('muted', '')
        this.$refs.videoEl.setAttribute('playsinline', '')
      }

      this.player.setLiveLowLatencyEnabled(true)
      this.player.attachHTMLVideoElement(this.$refs.videoEl)
      this.bindDomListeners()
      this.loadPlaybackSource()

      this.player.addEventListener(window.IVSPlayer.PlayerEventType.ERROR, this.handlePlayerError)
      this.player.addEventListener(window.IVSPlayer.PlayerState.READY, this.handlePlayerReady)
    }
  },
  beforeDestroy () {
    clearInterval(this.intervalId)
    this.clearRetryTimer()

    if (this.player) {
      this.player.delete()
    }

    if (this.$refs.videoEl) {
      this.$refs.videoEl.removeEventListener('play', this.handleVideoPlay)
      this.$refs.videoEl.removeEventListener('pause', this.handleVideoPause)
      this.$refs.videoEl.removeEventListener('playing', this.handleVideoPlaying)
    }

    if (this.containerEl) {
      this.containerEl.removeEventListener('dblclick', this.handleContainerDoubleClick)
    }

    // Удаляем слушатели событий
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange)

    // Выходим из fullscreen если он активен
    if (this.isFullscreen) {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      }
    }
  }
}
</script>
